import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Download, Upload, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function BackupPage() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [file, setFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const inputRef = useRef(null);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const name = m ? m[1] : `tmgd-tam-yedek-${Date.now()}.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tam yedek indirildi.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Yedek indirilemedi.");
    } finally { setDownloading(false); }
  };

  const doRestore = async () => {
    if (!file) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", "true");
      const { data } = await api.post("/admin/backup/restore", fd);
      const cols = Object.keys(data.restored || {}).length;
      toast.success(`Geri yükleme tamamlandı (${cols} koleksiyon). Güvenlik yedeği alındı.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Geri yükleme başarısız.");
    } finally {
      setRestoring(false);
      setConfirmOpen(false);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Yedekleme ve Geri Yükleme</h1>
        <p className="text-sm text-slate-500 mt-1">
          Yalnızca Ana Yönetici erişebilir. Yedek; kullanıcılar, firmalar, kayıtlar, kataloglar,
          dosyalar/sürümler, klasörler, çöp kutusu ve ayarları içerir. Gizli anahtarlar ve SMTP parolaları
          (ortam dosyasında tutulur) yedeğe dahil edilmez.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-display text-base">Tam Yedek İndir</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Tüm verilerin ve yüklenen dosyaların bütünlük imzalı (SHA-256) tek dosya yedeğini indirir.
            </p>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={downloadBackup} disabled={downloading} data-testid="backup-download-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Tam Yedek İndir
            </Button>
          </CardContent>
        </Card>

        <Card className="border-rose-200">
          <CardHeader><CardTitle className="font-display text-base text-rose-700 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Yedekten Geri Yükle</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Geri yükleme öncesi paket doğrulanır ve mevcut durumun güvenlik yedeği alınır. Veriler
              yedeğin alındığı ana döner. Hatalı paket mevcut veriyi değiştirmez.
            </p>
            <Input ref={inputRef} type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="backup-file-input" />
            <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
              disabled={!file} onClick={() => setConfirmOpen(true)} data-testid="backup-restore-btn">
              <Upload className="h-4 w-4 mr-2" /> Yedekten Geri Yükle
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-rose-700">Geri Yüklemeyi Onayla</DialogTitle>
            <DialogDescription>
              Tüm veriler seçtiğiniz yedeğin alındığı ana dönecektir. Devam etmeden önce mevcut durumun
              güvenlik yedeği otomatik olarak alınacaktır. Bu işlemi onaylıyor musunuz?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={restoring}>Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doRestore} disabled={restoring} data-testid="backup-restore-confirm">
              {restoring && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Evet, Geri Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
